
import React, { useState, useEffect } from 'react';
import { useHistory, Link } from 'react-router-dom';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    IconButton, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon, CheckCircle as ActivateIcon, Search as SearchIcon, Info as Info } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { fetchData, deleteData, activateData, postData } from '../../../apiService'; // Ensure postData is defined
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import CircularProgress from '@mui/material/CircularProgress';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import AddIcon from '@mui/icons-material/Add';
import CopyrightIcon from '@mui/icons-material/Copyright';
import RefreshIcon from '@mui/icons-material/Refresh';
import ApiIcon from '@mui/icons-material/Api';
import { Tooltip } from '@mui/material';

function NewPurchaseIndex() {
    const [data, setData] = useState([]);
    const [deleteItemId, setDeleteItemId] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchInput, setShowSearchInput] = useState(false);
    const history = useHistory();
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [isFocused, setIsFocused] = useState(false);

    const { isAuthenticated, companyId, branchId, permissions, token, logout, userId } = useAuth();
    const hasCreateMasterPermission = permissions.includes('Create Transaction');
    const hasEditMasterPermission = permissions.includes('Edit Transaction');
    const hasDeleteMasterPermission = permissions.includes('Delete Transaction');
    const [isLoading, setIsLoading] = useState(false);
    const [importDialogOpen, setImportDialogOpen] = useState(false);
    const [fileData, setFileData] = useState([]);

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        setFromDate(getFormattedStartDate());
        setToDate(getFormattedCurrentDate());
        fetchtableData();
    }, [isAuthenticated, companyId, branchId, token, logout]);


    const handleSearch = (event) => {
        setSearchQuery(event.target.value);
    };

    const toggleSearchInput = () => {
        setShowSearchInput(!showSearchInput);
    };

    const filteredData = data.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const getFormattedCurrentDate = () => {
        const currentDate = new Date();
        return currentDate.toISOString().split('T')[0];
    };

    const getFormattedStartDate = () => {
        const monthStartDate = new Date();
        monthStartDate.setDate(1);
        return monthStartDate.toISOString().split('T')[0];
    };


    const handleFromDateChange = (e) => {
        const selectedDate = e.target.value;
        setFromDate(selectedDate);
    };

    const handleToDateChange = (e) => {
        const selectedDate = e.target.value;
        setToDate(selectedDate);
    };

    const fetchtableData = async () => {

        setData([]);
        try {
            const response = await fetchData('PurchaseApi', `/PurchaseDetails/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
            if (response.responseCode === '1111') {
                setData(response.response_data);
                setFilteredData(response.response_data);
            }
        } catch (error) {
            console.error('Error fetching Customer Details:', error);
            if (error.response && error.response.status === 401) {
                logout();
                history.replace(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
            }
        }

    };

    const handleDelete = async () => {
        deleteData('PurchaseApi', `${deleteItemId}`, token)
            .then((response) => {
                fetchtableData();
                setDeleteItemId(null);
                setConfirmDialogOpen(false);
            })
            .catch((error) => {
                console.error('Error deleting record:', error);
            });
    };

    const handleActivate = (itemId) => {
        activateData('PurchaseApi', `${itemId}`, token)
            .then(() => {
                fetchtableData();
            })
            .catch((error) => {
                console.error('Error activating record:', error);
            });
    };

    const openConfirmDialog = (itemId) => {
        setDeleteItemId(itemId);
        setConfirmDialogOpen(true);
    };

    const closeConfirmDialog = () => {
        setDeleteItemId(null);
        setConfirmDialogOpen(false);
    };

    const openImportDialog = () => {
        setImportDialogOpen(true);
    };

    const closeImportDialog = () => {
        setImportDialogOpen(false);
        setFileData([]);
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        const reader = new FileReader();


        reader.onload = (e) => {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            const sheetData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

            const rowsToRead = sheetData.slice(5); // Read from the 5th row

            const formattedData = rowsToRead
                .filter(row => row[2]) // Skip rows where row[2] (BillingDate) is blank
                .map((row) => {

                    const currentDate = new Date();
                    const year = currentDate.getFullYear();
                    const month = String(currentDate.getMonth() + 1).padStart(2, '0');
                    const day = String(currentDate.getDate()).padStart(2, '0');
                    const formattedDate = `${year}-${month}-${day}`;

                    let billingDate = formattedDate;//row[2];
                    // Convert "DD.MM.YYYY" to ISO 8601 format
                    if (billingDate) {
                        //const [day, month, year] = billingDate.split('.');
                        //const date = new Date(`${year}-${month}-${day}T00:00:00Z`);
                        //billingDate = !isNaN(billingDate) ? billingDate.toISOString() : null;
                    }

                    return {
                        BillingDate: billingDate,
                        BillDoc: row[4] ? String(row[4]) : "", // Ensure BillDoc is a string
                        Payer: row[5] ? String(row[5]) : "", // Ensure Payer is a string
                        Name: row[6] ? String(row[6]) : "", // Ensure Name is a string
                        Item: row[7] ? String(row[7]) : "", // Ensure Item is a string
                        Material: row[8] ? String(row[8]) : "", // Ensure Material is a string
                        Description: row[9] ? String(row[9]) : "", // Ensure Description is a string
                        ProductHierarchy: row[10] ? String(row[10]) : "", // Ensure ProductHierarchy is a string
                        Status: row[11] ? String(row[11]) : "", // Ensure Status is a string
                        Qty: row[12] ?? 0,
                        BilledQuantity: row[13] ?? 0,
                        RRP: row[14] ?? 0,
                        ZV05: row[15] ?? 0,
                        ConcessionFee: row[16] ?? 0,
                        NetSalesII: row[17] ?? 0,
                        Vat: row[18] ?? 0,
                        GrossSales: row[19] ?? 0
                    };
                });

            setFileData(formattedData);

        };

        reader.readAsArrayBuffer(file);
    };

    const handleImport = () => {

        setIsLoading(true);

        const CreateData = {
            CompanyId: companyId,
            BranchId: branchId,
            CreatedBy: userId,
            PurchaseImport: fileData
        };

        postData('PurchaseImportApi', CreateData, token)
            .then((response) => {
                fetchtableData();
                setIsLoading(false);
                closeImportDialog();
                console.log('Data posted successfully:', response);
            })
            .catch((error) => {
                console.error('Error posting data:', error);
                setIsLoading(false);
            });
    };

    function formatDate(dateString) {
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        const formattedDate = new Date(dateString).toLocaleDateString('en-GB', options);
        return formattedDate;
    }

    return (
        <div className="w-full  mx-auto" style={{ width: '90%', marginTop: '10px' }}>
            <div className='w-full mb-2 flex'>
                <h6 className='block uppercase tracking-wide text-black productHead flex-grow linkcolor' style={{ fontFamily: 'Georgia, Cambria, "Times New Roman", Times, serif' }}>Purchase </h6>
                <div style={{ marginLeft: '-30px', marginBottom: '10px', display: 'flex', alignItems: 'center' }}>
                    <input
                        className="shadow text-gray-700 leading-tight"
                        type="text"
                        placeholder="Search..."
                        value={searchQuery}
                        onChange={handleSearch}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                        style={{
                            width: isFocused ? '270px' : '200px',
                            height: '30px',
                            border: '1px solid #ccc',
                            borderRadius: '15px',
                            padding: '0 20px',
                            transition: 'width 0.4s ease-in-out, border-color 0.3s ease-in-out',
                            outline: 'none',
                            borderColor: isFocused ? 'darkcyan' : '#ccc',
                            boxShadow: isFocused ? '0 4px 8px rgba(0, 0, 0, 0.2)' : 'none',
                            marginTop: '15px',
                        }}
                    />
                </div>
                <div style={{ marginRight: '2', maxWidth: '150px', marginLeft: '10px' }}>
                    <label className="block uppercase tracking-wide text-black text-xs font-bold mb-2" htmlFor="gstNumber" style={{ fontSize: '10px' }}>
                        From Date
                    </label>
                    <input
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline h-8"
                        type="date" value={fromDate} onChange={handleFromDateChange}
                        style={{ marginTop: '-10px', shadow: 'appearance-none', border: 'rounded', width: '100%', paddingY: '2', paddingX: '3', color: 'gray-700', leadingTight: true, focus: { color: 'gray-600', shadow: 'outline' } }}
                    />
                </div>
                <div style={{ marginRight: '2', maxWidth: '150px', marginLeft: '10px' }}>
                    <label className="block uppercase tracking-wide text-black text-xs font-bold mb-2" htmlFor="gstNumber" style={{ fontSize: '10px' }}>
                        To Date
                    </label>
                    <input
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline h-8"
                        type="date" value={toDate} onChange={handleToDateChange}
                        style={{ marginTop: '-10px', shadow: 'appearance-none', border: 'rounded', width: '100%', paddingY: '2', paddingX: '3', color: 'gray-700', leadingTight: true, focus: { color: 'gray-600', shadow: 'outline' } }}
                    />
                </div>
                <Button
                    style={{ background: 'linear-gradient(to right, #041815ff, #045157ff)', marginLeft: '8px', color: 'white', fontWeight: 'bolder', height: '30px', fontSize: '12px', textTransform: 'uppercase', marginTop: '20px', borderRadius: '25px' }}
                    onClick={fetchtableData}
                    className="linkcolor"
                >
                    Refresh
                </Button>

            </div>

            <div className="watermark">
                <CopyrightIcon /><b style={{ fontFamily: 'Script Bold Italic' }}>EFFTABS SOLUTIONS LLP</b>
            </div>

            <div className='w-full mb-2 flex' style={{ maxHeight: '520px', overflowY: 'auto' }}>
                <TableContainer component={Paper}>
                    <Table stickyHeader>
                        <TableHead sx={{ backgroundColor: '#58A5B3' }}>
                            <TableRow>
                                <TableCell
                                    style={{
                                        backgroundColor: '#27445D', borderBottom: '1px solid #F5F5F5', borderRight: '1px solid grey',
                                        borderRadius: '5px', color: 'white', width: '2%',
                                        fontWeight: '600', fontSize: '11px', border: '1px solid #ccc', textAlign: 'center', position: 'sticky',
                                        top: 0, zIndex: 10, whiteSpace: 'nowrap', padding: '3px', borderRadius: '4px', textTransform: 'uppercase'
                                    }}>
                                    SL NO
                                </TableCell>
                                <TableCell
                                    style={{
                                        backgroundColor: '#27445D', borderBottom: '1px solid #F5F5F5', borderRight: '1px solid grey',
                                        borderRadius: '5px', color: 'white', width: '5%',
                                        fontWeight: '600', fontSize: '11px', border: '1px solid #ccc', textAlign: 'center', position: 'sticky',
                                        top: 0, zIndex: 10, whiteSpace: 'nowrap', padding: '3px', borderRadius: '4px', textTransform: 'uppercase'
                                    }}>
                                    Bill DATE
                                </TableCell>
                                <TableCell
                                    style={{
                                        backgroundColor: '#27445D', borderBottom: '1px solid #F5F5F5', borderRight: '1px solid grey',
                                        borderRadius: '5px', color: 'white', width: '7%',
                                        fontWeight: '600', fontSize: '11px', border: '1px solid #ccc', textAlign: 'center', position: 'sticky',
                                        top: 0, zIndex: 10, whiteSpace: 'nowrap', padding: '3px', borderRadius: '4px', textTransform: 'uppercase'
                                    }}>
                                    Bill No
                                </TableCell>
                                <TableCell
                                    style={{
                                        backgroundColor: '#27445D', borderBottom: '1px solid #F5F5F5', borderRight: '1px solid grey',
                                        borderRadius: '5px', color: 'white', width: '10%',
                                        fontWeight: '600', fontSize: '11px', border: '1px solid #ccc', textAlign: 'center', position: 'sticky',
                                        top: 0, zIndex: 10, whiteSpace: 'nowrap', padding: '3px', borderRadius: '4px', textTransform: 'uppercase'
                                    }}>
                                    Supplier
                                </TableCell>

                                <TableCell
                                    style={{
                                        backgroundColor: '#27445D', borderBottom: '1px solid #F5F5F5', borderRight: '1px solid grey',
                                        borderRadius: '5px', color: 'white', width: '5%',
                                        fontWeight: '600', fontSize: '11px', border: '1px solid #ccc', textAlign: 'center', position: 'sticky',
                                        top: 0, zIndex: 10, whiteSpace: 'nowrap', padding: '3px', borderRadius: '4px', textTransform: 'uppercase'
                                    }}>

                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody sx={{ backgroundColor: 'whitesmoke' }}>
                            {filteredData.map((item, index) => (
                                <TableRow key={item.id} className={item.IsActive ? '' : 'inactiveRow'}
                                    sx={{
                                        '&:hover': {
                                            backgroundColor: '#dff1efff',
                                            cursor: 'pointer'
                                        },
                                    }}
                                >
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', padding: '3px', width: '2%', fontSize: '12px' }}>{index + 1}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', padding: '3px', width: '5%', fontSize: '12px' }}>{formatDate(item.BillDate)}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', padding: '3px', width: '7%', fontSize: '12px' }}>{item.BillNumber}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', padding: '3px', width: '10%', fontSize: '12px' }}>{item.SupplierName}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', padding: '3px', textAlign: 'right', width: '5%', fontSize: '12px' }}>
                                        <IconButton component={Link} to={`/NewPurchaseDetails/${item.Id}`} color="warning">
                                            <Tooltip
                                                title="Purchase Details" arrow>
                                                <Info style={{ fontSize: '17px' }} />
                                            </Tooltip>
                                        </IconButton>
                                        {hasEditMasterPermission && item.IsActive && (
                                            <Tooltip
                                                title="Purchase Return" arrow>
                                                <IconButton component={Link} to={`/NewPurchaseReturnCreate/${item.Id}`} color="primary">
                                                    <ApiIcon style={{ fontSize: '17px' }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {hasEditMasterPermission && item.IsActive && (
                                            <IconButton component={Link} to={`/NewPurchaseEdit/${item.Id}`} color="primary">
                                                <Tooltip
                                                    title="Purchase Edit" arrow>
                                                    <EditIcon style={{ fontSize: '17px' }} />
                                                </Tooltip>
                                            </IconButton>
                                        )}
                                        {hasDeleteMasterPermission && item.IsActive && (
                                            <IconButton onClick={() => openConfirmDialog(item.Id)} color="error">
                                                <Tooltip
                                                    title="Delete Purchase" arrow>
                                                    <DeleteIcon style={{ fontSize: '17px' }} />
                                                </Tooltip>
                                            </IconButton>
                                        )}
                                        {!item.IsActive && (
                                            <IconButton onClick={() => handleActivate(item.Id)} color="success">
                                                <ActivateIcon style={{ fontSize: '17px' }} />
                                            </IconButton>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>

            {/* Confirmation Dialog */}

            <Dialog
                open={confirmDialogOpen}
                onClose={closeConfirmDialog}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">{"Delete Record?"}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Are you sure you want to delete this record?
                    </DialogContentText>
                </DialogContent>

                <DialogActions>
                    <button
                        onClick={closeConfirmDialog}
                        style={{
                            backgroundColor: '#dc3545',
                            color: '#ffffff',
                            border: '1px solid #dc3545',
                            borderRadius: '4px',
                            padding: '8px 16px',
                            cursor: 'pointer',
                            marginRight: '8px',
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleDelete}
                        style={{
                            backgroundColor: '#28a745',
                            color: '#ffffff',
                            border: '1px solid #28a745',
                            borderRadius: '4px',
                            padding: '8px 16px',
                            cursor: 'pointer',
                        }}
                    >
                        Confirm
                    </button>
                </DialogActions>

            </Dialog>

            {/* Import Dialog */}
            <Dialog
                open={importDialogOpen}
                onClose={closeImportDialog}
                aria-labelledby="import-dialog-title"
                aria-describedby="import-dialog-description"
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle id="import-dialog-title">Import Purchase</DialogTitle>
                <DialogContent>
                    <DialogContentText id="import-dialog-description">
                        Select an Excel file to import purchase data. The data will be previewed before importing.
                    </DialogContentText>
                    <input
                        type="file"
                        accept=".xlsx, .xls"
                        onChange={handleFileChange}
                        style={{ marginTop: '16px' }}
                    />
                    {fileData.length > 0 && (
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead sx={{ backgroundColor: '#f3f3f3' }}>
                                    <TableRow>
                                        {/* Add table headers for the preview data */}
                                        <TableCell sx={{ height: '30px', padding: '10px', borderBottom: '1px solid #ddd', width: '15%' }}>Billing Date</TableCell>
                                        <TableCell sx={{ height: '30px', padding: '10px', borderBottom: '1px solid #ddd', width: '15%' }}>Bill Doc</TableCell>
                                        <TableCell sx={{ height: '30px', padding: '10px', borderBottom: '1px solid #ddd', width: '15%' }}>Payer</TableCell>
                                        <TableCell sx={{ height: '30px', padding: '10px', borderBottom: '1px solid #ddd', width: '15%' }}>Name</TableCell>
                                        <TableCell sx={{ height: '30px', padding: '10px', borderBottom: '1px solid #ddd', width: '15%' }}>Item</TableCell>
                                        <TableCell sx={{ height: '30px', padding: '10px', borderBottom: '1px solid #ddd', width: '15%' }}>Material</TableCell>
                                        <TableCell sx={{ height: '30px', padding: '10px', borderBottom: '1px solid #ddd', width: '15%' }}>Description</TableCell>
                                        <TableCell sx={{ height: '30px', padding: '10px', borderBottom: '1px solid #ddd', width: '15%' }}>Product Hierarchy</TableCell>
                                        <TableCell sx={{ height: '30px', padding: '10px', borderBottom: '1px solid #ddd', width: '15%' }}>Status</TableCell>
                                        <TableCell sx={{ height: '30px', padding: '10px', borderBottom: '1px solid #ddd', width: '15%' }}>Qty</TableCell>
                                        <TableCell sx={{ height: '30px', padding: '10px', borderBottom: '1px solid #ddd', width: '15%' }}>Billed Quantity</TableCell>
                                        <TableCell sx={{ height: '30px', padding: '10px', borderBottom: '1px solid #ddd', width: '15%' }}>RRP</TableCell>
                                        <TableCell sx={{ height: '30px', padding: '10px', borderBottom: '1px solid #ddd', width: '15%' }}>ZV05</TableCell>
                                        <TableCell sx={{ height: '30px', padding: '10px', borderBottom: '1px solid #ddd', width: '15%' }}>Concession Fee</TableCell>
                                        <TableCell sx={{ height: '30px', padding: '10px', borderBottom: '1px solid #ddd', width: '15%' }}>Net Sales II</TableCell>
                                        <TableCell sx={{ height: '30px', padding: '10px', borderBottom: '1px solid #ddd', width: '15%' }}>Vat</TableCell>
                                        <TableCell sx={{ height: '30px', padding: '10px', borderBottom: '1px solid #ddd', width: '15%' }}>Gross Sales</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {fileData.map((row, index) => (
                                        <TableRow key={index}>
                                            <TableCell sx={{ height: '30px', padding: '5px', width: '15%' }}>{formatDate(row.BillingDate)}</TableCell>
                                            <TableCell sx={{ height: '30px', padding: '5px', width: '15%' }}>{row.BillDoc}</TableCell>
                                            <TableCell sx={{ height: '30px', padding: '5px', width: '15%' }}>{row.Payer}</TableCell>
                                            <TableCell sx={{ height: '30px', padding: '5px', width: '15%' }}>{row.Name}</TableCell>
                                            <TableCell sx={{ height: '30px', padding: '5px', width: '15%' }}>{row.Item}</TableCell>
                                            <TableCell sx={{ height: '30px', padding: '5px', width: '15%' }}>{row.Material}</TableCell>
                                            <TableCell sx={{ height: '30px', padding: '5px', width: '15%' }}>{row.Description}</TableCell>
                                            <TableCell sx={{ height: '30px', padding: '5px', width: '15%' }}>{row.ProductHierarchy}</TableCell>
                                            <TableCell sx={{ height: '30px', padding: '5px', width: '15%' }}>{row.Status}</TableCell>
                                            <TableCell sx={{ height: '30px', padding: '5px', width: '15%' }}>{row.Qty}</TableCell>
                                            <TableCell sx={{ height: '30px', padding: '5px', width: '15%' }}>{row.BilledQuantity}</TableCell>
                                            <TableCell sx={{ height: '30px', padding: '5px', width: '15%' }}>{row.RRP}</TableCell>
                                            <TableCell sx={{ height: '30px', padding: '5px', width: '15%' }}>{row.ZV05}</TableCell>
                                            <TableCell sx={{ height: '30px', padding: '5px', width: '15%' }}>{row.ConcessionFee}</TableCell>
                                            <TableCell sx={{ height: '30px', padding: '5px', width: '15%' }}>{row.NetSalesII}</TableCell>
                                            <TableCell sx={{ height: '30px', padding: '5px', width: '15%' }}>{row.Vat}</TableCell>
                                            <TableCell sx={{ height: '30px', padding: '5px', width: '15%' }}>{row.GrossSales}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="contained"
                        onClick={closeImportDialog} color="primary">Cancel
                    </Button>

                    <Button variant="contained" onClick={handleImport} color="secondary" disabled={isLoading || fileData.length === 0}>
                        {isLoading ? <CircularProgress size={24} color="inherit" /> : "Import"}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default NewPurchaseIndex;



